// File: src/components/common/Modal.tsx
import React, { ReactNode }  from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string | ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: ReactNode;
  showCloseButton?: boolean;
  glassEffect?: boolean; // NEW: Enable Glass Morphism effects
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, size = 'md', footer, showCloseButton = true, glassEffect = false }) => {
  if (!isOpen) return null;

  let sizeClasses = 'max-w-md'; // default md
  if (size === 'sm') sizeClasses = 'max-w-sm';
  if (size === 'lg') sizeClasses = 'max-w-lg';
  if (size === 'xl') sizeClasses = 'max-w-xl';

  // Glass Morphism styles
  const glassBackdropClasses = glassEffect 
    ? "fixed inset-0 z-[1000] flex items-center justify-center bg-gradient-to-br from-cyan-200/30 via-blue-200/25 to-teal-400/30 backdrop-blur-lg p-4 overflow-hidden"
    : "fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 overflow-hidden";

  const glassModalClasses = glassEffect
    ? `bg-gradient-to-br from-cyan-50/20 via-blue-100/15 to-white/25 backdrop-blur-xl border border-white/40 shadow-2xl rounded-2xl w-full ${sizeClasses} flex flex-col max-h-[90vh] sm:max-h-[85vh] overflow-hidden`
    : `bg-white rounded-lg shadow-xl w-full ${sizeClasses} flex flex-col max-h-[90vh] sm:max-h-[85vh] overflow-hidden`;

  return (
    // Backdrop: covers the entire screen, now handles outside clicks
    <div 
      className={glassBackdropClasses}
      onClick={onClose}
    > 
      
      {/* Modal Dialog: constrained height, flex column layout */}
      <div 
        className={glassModalClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Modal Header: remains fixed */}
        <div className={`flex items-center justify-between p-4 sm:p-5 flex-shrink-0 ${glassEffect ? 'border-b border-white/50 bg-gradient-to-r from-cyan-600/50 to-blue-500/40 backdrop-blur-md' : 'border-b border-neutral-light'}`}> 
          <h3 id="modal-title" className={`text-xl font-semibold ${glassEffect ? 'text-white drop-shadow-lg' : 'text-neutral-darkest'}`}>{title}</h3>
          {showCloseButton && (
            <button
              onClick={onClose}
              className={`p-1 rounded-full -mr-1 focus:outline-none focus:ring-2 transition-colors ${
                glassEffect 
                  ? 'text-white/90 hover:text-white hover:bg-white/25 focus:ring-white/50 backdrop-blur-sm' 
                  : 'text-neutral-dark hover:text-neutral-darkest focus:ring-primary'
              }`}
              aria-label="Close modal"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>
        
        {/* Modal Content: scrollable area */}
        <div className={`overflow-y-auto flex-grow p-4 sm:p-5 ${glassEffect ? 'relative z-10' : ''}`}> 
          {children}
        </div>

        {footer && (
          <div className={`p-4 sm:p-5 flex-shrink-0 ${glassEffect ? 'border-t border-white/50 bg-gradient-to-r from-cyan-200/20 to-primary-light/20 backdrop-blur-md' : 'border-t border-neutral-light'}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;